import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CalendarClock, CheckCircle2, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookCallDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({
    full_name: "",
    business_name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form"); // form | booking
  const [bookingUrl, setBookingUrl] = useState("");

  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API}/config`)
      .then((r) => setBookingUrl(r.data?.google_booking_url || ""))
      .catch(() => setBookingUrl(""));
  }, [open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads/book`, {
        full_name: form.full_name,
        business_name: form.business_name || null,
        email: form.email,
        phone: form.phone || null,
        notes: form.notes || null,
      });
      setStep("booking");
      toast.success("Details received — pick a time that suits you.");
    } catch (err) {
      toast.error("Couldn’t submit — try again or email paul@pipelineforge.co.uk.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (v) => {
    onOpenChange(v);
    if (!v) {
      setTimeout(() => {
        setStep("form");
        setForm({ full_name: "", business_name: "", email: "", phone: "", notes: "" });
      }, 250);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="booking-dialog"
        className="max-w-2xl bg-[#0a0a0a] border-white/10 text-white rounded-none p-0 max-h-[92vh] overflow-y-auto"
      >
        {step === "form" ? (
          <form onSubmit={handleSubmit} className="p-8" data-testid="booking-step-form">
            <DialogHeader>
              <div className="text-xs uppercase tracking-[0.25em] text-[#ccff00] font-semibold mb-3">
                / Book a 20-min call
              </div>
              <DialogTitle className="font-display text-3xl uppercase">
                Talk to a strategist.
              </DialogTitle>
              <DialogDescription className="text-white/60 pt-2">
                Tell us who you are. Next, you’ll pick a time on Paul’s live calendar.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              <Field label="Full name *">
                <Input
                  data-testid="booking-full-name"
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  placeholder="Jane Smith"
                  className="bg-[#050505] border-white/15 rounded-none h-11 focus-visible:ring-[#ccff00] focus-visible:ring-1"
                />
              </Field>
              <Field label="Email *">
                <Input
                  data-testid="booking-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@acme.co"
                  className="bg-[#050505] border-white/15 rounded-none h-11 focus-visible:ring-[#ccff00] focus-visible:ring-1"
                />
              </Field>
              <Field label="Business name">
                <Input
                  data-testid="booking-business-name"
                  value={form.business_name}
                  onChange={(e) => update("business_name", e.target.value)}
                  placeholder="Acme Co."
                  className="bg-[#050505] border-white/15 rounded-none h-11 focus-visible:ring-[#ccff00] focus-visible:ring-1"
                />
              </Field>
              <Field label="Phone (optional)">
                <Input
                  data-testid="booking-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="+44 7700 900000"
                  className="bg-[#050505] border-white/15 rounded-none h-11 focus-visible:ring-[#ccff00] focus-visible:ring-1"
                />
              </Field>
            </div>

            <div className="mt-4">
              <Field label="Anything we should prep?">
                <Textarea
                  data-testid="booking-notes"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  placeholder="One sentence is fine."
                  rows={3}
                  className="bg-[#050505] border-white/15 rounded-none focus-visible:ring-[#ccff00] focus-visible:ring-1"
                />
              </Field>
            </div>

            <button
              type="submit"
              data-testid="booking-form-submit"
              disabled={loading}
              className="mt-6 w-full h-12 bg-[#ccff00] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#b3e600] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "Submitting…" : "Continue to calendar →"}
            </button>
          </form>
        ) : (
          <div className="p-8" data-testid="booking-step-calendar">
            <DialogHeader>
              <div className="text-xs uppercase tracking-[0.25em] text-[#ccff00] font-semibold mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Details received
              </div>
              <DialogTitle className="font-display text-3xl uppercase">
                Pick your time.
              </DialogTitle>
              <DialogDescription className="text-white/60 pt-2">
                Choose a slot on Paul’s live calendar. You’ll get an instant
                confirmation and invite by email.
              </DialogDescription>
            </DialogHeader>

            {bookingUrl ? (
              <div className="mt-5">
                <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.2em] text-white/50">
                  <span className="w-1.5 h-1.5 bg-[#ccff00]" />
                  All times shown in GMT (UK time)
                </div>
                <div className="border border-white/10 bg-white overflow-hidden">
                  <iframe
                    data-testid="booking-calendar-iframe"
                    src={`${bookingUrl}${bookingUrl.includes("?") ? "&" : "?"}gv=true`}
                    title="Book a call with PipelineForge"
                    className="w-full"
                    style={{ height: "620px", border: 0 }}
                  />
                </div>
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booking-open-newtab"
                  className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-[#ccff00] transition-colors"
                >
                  Open calendar in a new tab <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            ) : (
              <div
                data-testid="booking-no-calendar"
                className="mt-6 border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center"
              >
                <CalendarClock className="w-10 h-10 text-[#ccff00] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-white/80 font-semibold mb-1">You’re all set.</p>
                <p className="text-white/60 text-sm max-w-sm mx-auto leading-relaxed">
                  We’ve emailed you a link to book your call and Paul has been
                  notified. Check your inbox — we’ll be in touch within the hour.
                </p>
              </div>
            )}

            <button
              data-testid="booking-dialog-close"
              onClick={() => handleClose(false)}
              className="mt-6 w-full h-11 border border-white/20 text-white font-semibold uppercase tracking-wider text-xs hover:border-white/50 transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-white/60">{label}</Label>
      {children}
    </div>
  );
}
